import { Box, COLOR_PALETTE, Flex, Icon, Text, desktopFirstMediaQuery } from '@inflearn/ds-react';
import { faStar } from '@inflearn/pro-solid-svg-icons';
import { useTranslation } from 'next-i18next';

import type { ReactNode } from 'react';
import type { MentorDto } from '../../../../resources/@types/uccSchema';
import { formatCommaNumber } from '../../../utils/format/number';

type Props = {
  course: {
    studentCount: number;
    reviewCount: number;
    reviewAverage: string;
    courseCount: number;
  };
  mentor?: MentorDto;
};

export function InstructorInformation({ course, mentor }: Props) {
  const { t } = useTranslation();
  const { studentCount, reviewCount, reviewAverage, courseCount } = course;

  const isNoData = (value: number | string) => Number(value) === 0;
  const hideCourseData = isNoData(courseCount) && !!mentor;

  return (
    <Flex
      py={12}
      gap={16}
      direction={'column'}
      css={{
        borderTop: `1px solid ${COLOR_PALETTE.gray[2]}`,
        borderBottom: `1px solid ${COLOR_PALETTE.gray[2]}`,
        [desktopFirstMediaQuery.tablet]: {
          marginTop: 0,
        },
      }}
    >
      {!hideCourseData && (
        <Flex
          gap={8}
          css={{
            width: '100%',
            justifyContent: 'space-around',
          }}
        >
          <LabeledValue
            label={t('수강생수')}
            value={isNoData(studentCount) ? '-' : formatCommaNumber(studentCount)}
          />
          <LabeledValue
            label={t('수강평수')}
            value={isNoData(reviewCount) ? '-' : formatCommaNumber(reviewCount)}
          />
          <LabeledValue
            label={t('강의평점')}
            value={
              <>
                {isNoData(reviewAverage) ? null : <Icon icon={faStar} size="xs" color="yellow.6" />}
                <Text component="span" css={{ verticalAlign: 'middle' }} ml={2}>
                  {isNoData(reviewAverage) ? '-' : reviewAverage}
                </Text>
              </>
            }
          />
        </Flex>
      )}
      {mentor && (
        <Flex
          gap={8}
          css={{
            width: '100%',
            justifyContent: 'space-around',
          }}
        >
          <LabeledValue
            label={t('멘토링 신청')}
            value={isNoData(mentor.mentoringCount) ? '-' : formatCommaNumber(mentor.mentoringCount)}
          />
          <LabeledValue
            label={t('멘토링 리뷰')}
            value={isNoData(mentor.reviewCount) ? '-' : formatCommaNumber(mentor.reviewCount)}
          />
          <LabeledValue
            label={t('멘토링 평점')}
            value={
              <>
                {isNoData(mentor.reviewAverage) ? null : (
                  <Icon icon={faStar} size="xs" color="yellow.6" />
                )}
                <Text component="span" css={{ verticalAlign: 'middle' }} ml={2}>
                  {isNoData(mentor.reviewAverage) ? '-' : mentor.reviewAverage}
                </Text>
              </>
            }
          />
        </Flex>
      )}
    </Flex>
  );
}

function LabeledValue({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <Box>
      <Text size="xs" color="gray.6" weight={500} align={'center'}>
        {label}
      </Text>
      <Text size="sm" weight={600} color="black" align={'center'}>
        {value}
      </Text>
    </Box>
  );
}
